import React from "react";
import { formatNumber } from "../../utils/formatting";
import type { TreasuryResponse, TreasuryHistoryResponse } from "../../pages/AdminDashboard";

interface AdminTreasuryTabProps {
    treasury: TreasuryResponse | null;
    treasuryHistory: TreasuryHistoryResponse | null;
    loadingTreasury: boolean;
    loadingTreasuryHistory: boolean;
    fetchTreasury: (refresh?: boolean) => Promise<void>;
    fetchTreasuryHistory: () => Promise<void>;
}

const AdminTreasuryTab: React.FC<AdminTreasuryTabProps> = ({
    treasury,
    treasuryHistory,
    loadingTreasury,
    loadingTreasuryHistory,
    fetchTreasury,
    fetchTreasuryHistory,
}) => {
    return (
        <div className="glass-card" style={{ padding: "16px", borderRadius: "16px", display: "grid", gap: "12px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                <div style={{ color: "#9ca3af", fontSize: "12px" }}>
                    As of {treasury ? new Date(treasury.asOf).toLocaleString() : "-"}
                </div>
                <button
                    onClick={() => {
                        void fetchTreasury(true);
                        void fetchTreasuryHistory();
                    }}
                    style={{
                        padding: "8px 12px",
                        borderRadius: "8px",
                        border: "1px solid rgba(255,255,255,0.15)",
                        background: "rgba(255,255,255,0.06)",
                        color: "white",
                        cursor: "pointer",
                    }}
                >
                    Refresh Live Balances
                </button>
            </div>

            {loadingTreasury ? (
                <p style={{ color: "#9ca3af" }}>Loading treasury balances...</p>
            ) : treasury ? (
                <>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: "10px" }}>
                        <div
                            style={{
                                padding: "12px",
                                borderRadius: "12px",
                                background: "rgba(255,255,255,0.04)",
                                border: "1px solid rgba(255,255,255,0.08)",
                            }}
                        >
                            <div style={{ color: "#9ca3af", fontSize: "12px" }}>Treasury Wallets</div>
                            <div style={{ fontSize: "24px", fontWeight: 700 }}>{formatNumber(treasury.wallets.length, 0)}</div>
                        </div>
                        <div
                            style={{
                                padding: "12px",
                                borderRadius: "12px",
                                background: "rgba(255,255,255,0.04)",
                                border: "1px solid rgba(255,255,255,0.08)",
                            }}
                        >
                            <div style={{ color: "#9ca3af", fontSize: "12px" }}>Total SOL</div>
                            <div style={{ fontSize: "24px", fontWeight: 700 }}>{formatNumber(treasury.totalSol, 4)}</div>
                        </div>
                        <div
                            style={{
                                padding: "12px",
                                borderRadius: "12px",
                                background: "rgba(255,255,255,0.04)",
                                border: "1px solid rgba(255,255,255,0.08)",
                            }}
                        >
                            <div style={{ color: "#9ca3af", fontSize: "12px" }}>Latest Snapshot</div>
                            <div style={{ fontSize: "13px", marginTop: "6px" }}>
                                {treasury.snapshot ? `${formatNumber(treasury.snapshot.totalSol, 4)} SOL` : "No snapshot yet"}
                            </div>
                            <div style={{ fontSize: "12px", color: "#9ca3af", marginTop: "4px" }}>
                                {treasury.snapshot ? new Date(treasury.snapshot.snapshotAt).toLocaleString() : ""}
                            </div>
                        </div>
                    </div>

                    <div style={{ overflowX: "auto" }}>
                        <div style={{ display: "grid", gap: "10px", minWidth: "900px" }}>
                            {treasury.wallets.map((wallet) => (
                                <div
                                    key={wallet.address}
                                    style={{
                                        padding: "12px",
                                        borderRadius: "12px",
                                        background: "rgba(255,255,255,0.03)",
                                        border: "1px solid rgba(255,255,255,0.08)",
                                    }}
                                >
                                    <div style={{ display: "flex", justifyContent: "space-between", gap: "12px", marginBottom: "8px" }}>
                                        <div style={{ fontFamily: "monospace", fontSize: "12px" }}>{wallet.address}</div>
                                        <div style={{ color: "#14f195", fontWeight: 600 }}>{formatNumber(wallet.sol, 4)} SOL</div>
                                    </div>
                                    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
                                        {wallet.tokens.length === 0 && <span style={{ color: "#9ca3af", fontSize: "12px" }}>No token balances</span>}
                                        {wallet.tokens.slice(0, 10).map((token) => (
                                            <span
                                                key={`${wallet.address}:${token.mint}`}
                                                style={{
                                                    padding: "4px 8px",
                                                    borderRadius: "999px",
                                                    border: "1px solid rgba(255,255,255,0.12)",
                                                    fontSize: "11px",
                                                    color: "#d1d5db",
                                                }}
                                            >
                                                {token.symbol}: {formatNumber(token.amount, 4)}
                                            </span>
                                        ))}
                                        {wallet.tokens.length > 10 && (
                                            <span style={{ color: "#9ca3af", fontSize: "11px" }}>
                                                +{wallet.tokens.length - 10} more tokens
                                            </span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            ) : (
                <p style={{ color: "#9ca3af" }}>No treasury data loaded yet.</p>
            )}

            <div style={{ overflowX: "auto" }}>
                <div style={{ minWidth: "500px" }}>
                    <h3 style={{ marginBottom: "8px", fontSize: "15px" }}>Snapshot History</h3>
                    {loadingTreasuryHistory ? (
                        <p style={{ color: "#9ca3af" }}>Loading history...</p>
                    ) : (
                        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                            <thead>
                                <tr style={{ color: "#9ca3af", textAlign: "left" }}>
                                    <th style={{ padding: "8px" }}>Snapshot Time</th>
                                    <th style={{ padding: "8px" }}>Total SOL</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(treasuryHistory?.items || []).slice(0, 48).map((row) => (
                                    <tr key={row.snapshotAt} style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                                        <td style={{ padding: "8px" }}>{new Date(row.snapshotAt).toLocaleString()}</td>
                                        <td style={{ padding: "8px" }}>{formatNumber(row.totalSol, 4)}</td>
                                    </tr>
                                ))}
                                {(treasuryHistory?.items || []).length === 0 && (
                                    <tr>
                                        <td style={{ padding: "8px", color: "#9ca3af" }} colSpan={2}>
                                            No snapshots yet.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminTreasuryTab;
